import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Globe, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

export const Navbar = () => {
  const { t, lang, changeLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const text = (it, en) => lang === "it" ? it : en;
  const links = [
    { label: text("Come funziona", "How it works"), children: [
      { to: "/plans", label: text("Free e Pro", "Free and Pro") },
      { to: "/how-it-works", label: text("Per chi adotta", "For adopters") },
      { to: "/for-farmers", label: text("Per agricoltori e allevatori", "For farmers and livestock keepers") },
      { to: "/for-entities", label: text("Per enti e professionisti", "For institutions and professionals") },
    ] },
    { label: text("Atlante", "Atlas"), children: [
      { to: "/atlas", label: text("Tutti i soggetti", "All subjects") },
      { to: "/crops", label: text("Esplora per coltura", "Explore by crop") },
      { to: "/atlas?subject_type=farm", label: text("Aziende agricole", "Farms") },
      { to: "/atlas?subject_type=livestock", label: text("Allevamenti", "Livestock farms") },
      { to: "/atlas?subject_type=nursery", label: text("Vivai", "Nurseries") },
      { to: "/atlas?subject_type=public_body", label: text("Comuni ed enti pubblici", "Municipalities and public bodies") },
      { to: "/atlas?subject_type=park", label: text("Parchi e riserve", "Parks and reserves") },
      { to: "/atlas?subject_type=botanical_garden", label: text("Giardini, oasi e centri educativi", "Gardens, oases and educational centers") },
      { to: "/atlas?subject_type=professional,association", label: text("Professionisti, associazioni e fondazioni", "Professionals, associations and foundations") },
    ] },
    { label: text("Esplora adozioni", "Explore adoptions"), children: [
      { to: "/explore", label: text("Esplora la mappa", "Explore the map") },
      { to: "/explore?activity=agriculture&opportunity=ADOPT", label: text("Colture agricole", "Agricultural crops") },
      { to: "/explore?activity=gardens&opportunity=ADOPT", label: text("Orti", "Food gardens") },
      { to: "/explore?activity=livestock&opportunity=ADOPT", label: text("Animali e razze", "Animals and breeds") },
      { to: "/explore?activity=public&opportunity=ADOPT", label: text("Verde pubblico", "Public green spaces") },
      { to: "/explore?activity=environment&opportunity=ADOPT", label: text("Biodiversità e conservazione", "Biodiversity and conservation") },
    ] },
    { to: "/projects", label: text("Progetti", "Projects") },
    { label: text("Il progetto", "About PlantAdopting"), children: [
      { to: "/about", label: text("Chi siamo", "About us") },
      { to: "/about#mission", label: text("Missione e valori", "Mission and values") },
      { to: "/faq", label: text("Domande frequenti", "Frequently asked questions") },
      { to: "/support", label: text("Sostieni PlantAdopting", "Support PlantAdopting") },
    ] },
  ];

  return (
    <header
      className="sticky top-0 z-[1000] border-b border-[#E2DACB] bg-[#F8F5EA]/85 backdrop-blur-xl"
      data-testid="navbar"
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-6 h-[72px]">
          <Link className="shrink-0" to="/" data-testid="nav-logo-link">
            <Logo compact />
          </Link>

          <nav className="hidden 2xl:flex shrink-0 items-center gap-3 whitespace-nowrap">
            {links.map((l) => l.children ? (
              <DropdownMenu key={l.label}>
                <DropdownMenuTrigger className="font-serif text-xl font-semibold text-[#4A443D] hover:text-[#557448]">{l.label}</DropdownMenuTrigger>
                <DropdownMenuContent className="pa-navigation-menu z-[1200] bg-[#FFFDF8]">
                  {l.children.map((child) => <DropdownMenuItem asChild key={child.to}><Link to={child.to}>{child.label}</Link></DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : <Link key={l.to} to={l.to} className="font-serif text-xl font-semibold text-[#4A443D] hover:text-[#557448]">{l.label}</Link>)}
          </nav>

          <div className="hidden 2xl:flex shrink-0 items-center gap-3 whitespace-nowrap">
            <button
              aria-label={t("nav.search")}
              data-testid="nav-search-btn"
              onClick={() => navigate("/explore?focus=search")}
              className="p-2 rounded-full text-[#4A443D] hover:bg-[#EDE7D7] transition-colors duration-200"
            >
              <Search size={18} />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="lang-selector"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm font-medium text-[#4A443D] hover:bg-[#EDE7D7] transition-colors duration-200"
                >
                  <Globe size={16} />
                  {lang.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[8rem] z-[1100]">
                <DropdownMenuItem data-testid="lang-en" onClick={() => changeLang("en")}>
                  English
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="lang-it" onClick={() => changeLang("it")}>
                  Italiano
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="font-serif text-xl font-semibold text-[#4A443D] hover:text-[#557448] transition-colors duration-200 px-2"
                >
                  {t("auth.myDashboard")}
                </Link>
                <button
                  data-testid="nav-logout"
                  onClick={() => { logout(); navigate("/"); }}
                  className="font-serif text-xl font-semibold text-[#4A443D] hover:text-[#557448] transition-colors duration-200 px-2"
                >
                  {t("auth.logout")}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="font-serif text-xl font-semibold text-[#4A443D] hover:text-[#557448] transition-colors duration-200 px-2"
              >
                {t("auth.login")}
              </Link>
            )}

            <Button
              asChild
              data-testid="nav-list-farm"
              className="rounded-full bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA] font-sans font-medium px-5 shadow-none"
            >
              <Link to="/list-your-farm">{t("nav.presentYourReality")}</Link>
            </Button>
          </div>

          {/* Mobile */}
          <div className="2xl:hidden flex items-center gap-1">
            <button
              aria-label={t("nav.search")}
              data-testid="nav-search-btn-mobile"
              onClick={() => navigate("/explore?focus=search")}
              className="p-2 rounded-full text-[#4A443D]"
            >
              <Search size={20} />
            </button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button aria-label={t("nav.menu")} data-testid="nav-menu-btn" className="p-2 text-[#2A2521]">
                  <Menu size={24} />
                </button>
              </SheetTrigger>
              <SheetContent hideClose aria-describedby={undefined} side="right" className="bg-[#F8F5EA] border-l border-[#E2DACB] w-[85vw] max-w-sm z-[1200] p-0 overflow-y-auto">
                <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
                <div className="flex items-center justify-between px-5 h-[72px] border-b border-[#E2DACB]">
                  <Logo compact />
                  <button aria-label={text("Chiudi menu", "Close menu")} data-testid="nav-menu-close" onClick={() => setOpen(false)} className="p-2">
                    <X size={22} />
                  </button>
                </div>
                <div className="flex flex-col p-5 gap-1">
                  {links.map((l) => l.children ? (
                    <details key={l.label} className="border-b border-[#EDE7D7] py-2">
                      <summary className="cursor-pointer font-serif text-xl text-[#2A2521]">{l.label}</summary>
                      <div className="flex flex-col gap-3 pl-3 py-3">{l.children.map((child) => <Link key={child.to} to={child.to} onClick={() => setOpen(false)} className="font-serif text-xl font-medium text-[#4A443D]">{child.label}</Link>)}</div>
                    </details>
                  ) : <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-serif text-xl py-2 border-b border-[#EDE7D7]">{l.label}</Link>)}
                  <div className="flex items-center gap-2 mt-4">
                    <button
                      data-testid="mobile-lang-en"
                      onClick={() => changeLang("en")}
                      className={`px-4 py-2 rounded-full text-sm font-medium border ${lang === "en" ? "bg-[#557448] text-[#F8F5EA] border-[#557448]" : "border-[#E2DACB] text-[#4A443D]"}`}
                    >
                      EN
                    </button>
                    <button
                      data-testid="mobile-lang-it"
                      onClick={() => changeLang("it")}
                      className={`px-4 py-2 rounded-full text-sm font-medium border ${lang === "it" ? "bg-[#557448] text-[#F8F5EA] border-[#557448]" : "border-[#E2DACB] text-[#4A443D]"}`}
                    >
                      IT
                    </button>
                  </div>
                  <Button
                    asChild
                    className="rounded-full bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA] mt-5 h-11"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-list-farm"
                  >
                    <Link to="/list-your-farm">{t("nav.presentYourReality")}</Link>
                  </Button>
                  {user ? (
                    <>
                      <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="mobile-dashboard"
                        className="font-serif text-xl text-[#2A2521] py-2.5 mt-2 border-t border-[#EDE7D7]">
                        {t("auth.myDashboard")}
                      </Link>
                      <button data-testid="mobile-logout" onClick={() => { logout(); setOpen(false); navigate("/"); }}
                        className="font-serif text-xl text-left text-[#2A2521] py-2.5">
                        {t("auth.logout")}
                      </button>
                    </>
                  ) : (
                    <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-login"
                      className="font-serif text-xl text-[#2A2521] py-2.5 mt-2 border-t border-[#EDE7D7]">
                      {t("auth.login")}
                    </Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};



